import { GoogleGenAI } from "@google/genai";
import { delay } from "./utils";
import { GenerationResult } from "./types";

// --- Configuration ---

// Use process.env.REACT_APP_GEMINI_API_KEY for Create React App
const DEFAULT_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || '';

// Model Constants
const MODEL_IMAGE_EDIT = 'gemini-2.5-flash-image';
const MODEL_VEO_PREVIEW = 'veo-3.1-fast-generate-preview';

/**
 * Creates a GoogleGenAI instance.
 * For Veo, it attempts to ensure a selected API key is used if available in the environment context,
 * though strict adherence to `window.aistudio` flow happens in the component before calling this.
 */
const getAIClient = (apiKey: string = DEFAULT_API_KEY) => {
  return new GoogleGenAI({ apiKey });
};

export const generateVirtualTryOn = async (
  personBase64: string,
  garmentBase64: string,
  mimeType: string = 'image/jpeg'
): Promise<GenerationResult> => {
  const ai = getAIClient();
  
  const prompt = `Act as a professional fashion editor and image compositor.
Task: perform a photorealistic virtual try-on.
Input 1: A photo of a person (the model).
Input 2: A photo of a clothing item (the garment).
Instructions:
Synthesize a new image of the person from Input 1 wearing the garment from Input 2.
FIT & PHYSICS: The garment must drape naturally over the person's body shape. Respect gravity, folds, and fabric tension.
PRESERVATION: You MUST preserve the person's exact facial features, skin tone, hair style, and body pose. Do not alter the background.
LIGHTING: Retain the lighting and shadows from the person's original photo. The new garment must be lit by the same light sources.
QUALITY: The result should be indistinguishable from a real photograph.
Output: A high-resolution photo of the result only.`;

  try {
    const response = await ai.models.generateContent({
      model: MODEL_IMAGE_EDIT,
      contents: {
        parts: [
          { inlineData: { mimeType, data: personBase64 } },
          { inlineData: { mimeType, data: garmentBase64 } },
          { text: prompt },
        ],
      },
    });

    // Extract usage metadata
    const usage = response.usageMetadata ? {
      promptTokenCount: response.usageMetadata.promptTokenCount,
      candidatesTokenCount: response.usageMetadata.candidatesTokenCount,
      totalTokenCount: response.usageMetadata.totalTokenCount,
    } : undefined;

    // Extract image from response
    const candidates = response.candidates;
    if (candidates && candidates.length > 0 && candidates[0]?.content?.parts) {
      for (const part of candidates[0].content.parts) {
        if (part.inlineData) {
          return {
            mediaData: `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`,
            usage
          };
        }
      }
    }
    
    throw new Error("No image data found in response.");

  } catch (error) {
    console.error("Virtual Try-On Error:", error);
    throw error;
  }
};

export const editImageWithPrompt = async (
  imageBase64: string,
  promptText: string,
  mimeType: string = 'image/jpeg'
): Promise<GenerationResult> => {
  const ai = getAIClient();

  try {
    const response = await ai.models.generateContent({
      model: MODEL_IMAGE_EDIT,
      contents: {
        parts: [
          { inlineData: { mimeType, data: imageBase64 } },
          { text: promptText },
        ],
      },
    });

    const usage = response.usageMetadata ? {
        promptTokenCount: response.usageMetadata.promptTokenCount,
        candidatesTokenCount: response.usageMetadata.candidatesTokenCount,
        totalTokenCount: response.usageMetadata.totalTokenCount,
      } : undefined;

    const candidates = response.candidates;
    if (candidates && candidates.length > 0 && candidates[0]?.content?.parts) {
      for (const part of candidates[0].content.parts) {
        if (part.inlineData) {
          return {
             mediaData: `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`,
             usage
          };
        }
      }
    }
    
    throw new Error("No image data found in response.");

  } catch (error) {
    console.error("Magic Editor Error:", error);
    throw error;
  }
};

export const generateVeoVideo = async (
  imageBase64: string,
  promptText: string,
  aspectRatio: '16:9' | '9:16' = '16:9',
  mimeType: string = 'image/jpeg'
): Promise<string> => {
  // Veo requires the API key to be fresh from the selection dialog if updated.
  const ai = getAIClient(process.env.REACT_APP_GEMINI_API_KEY);

  try {
    console.log("Starting Veo generation...");
    let operation = await ai.models.generateVideos({
      model: MODEL_VEO_PREVIEW,
      prompt: promptText || "Animate this image naturally.",
      image: {
        imageBytes: imageBase64,
        mimeType: mimeType,
      },
      config: {
        numberOfVideos: 1,
        resolution: '720p',
        aspectRatio: aspectRatio,
      }
    });

    console.log("Veo Operation started:", operation);

    while (!operation.done) {
      await delay(5000);
      console.log("Polling Veo status...");
      operation = await ai.operations.getVideosOperation({ operation: operation });
    }

    if (operation.error) {
      throw new Error(`Veo Generation Failed: ${JSON.stringify(operation.error)}`);
    }

    const videoUri = operation.response?.generatedVideos?.[0]?.video?.uri;
    if (!videoUri) {
      throw new Error("No video URI returned from Veo operation.");
    }

    const videoResponse = await fetch(`${videoUri}&key=${process.env.REACT_APP_GEMINI_API_KEY}`);
    if (!videoResponse.ok) {
       throw new Error(`Failed to download video: ${videoResponse.statusText}`);
    }
    const videoBlob = await videoResponse.blob();
    return URL.createObjectURL(videoBlob);

  } catch (error) {
    console.error("Veo Generation Error:", error);
    throw error;
  }
};

export const ensureApiKeySelected = async (): Promise<void> => {
  if (window.aistudio && window.aistudio.hasSelectedApiKey) {
    const hasKey = await window.aistudio.hasSelectedApiKey();
    if (!hasKey) {
      await window.aistudio.openSelectKey();
    }
  } else {
    console.warn("window.aistudio not available, assuming environment key is sufficient for dev.");
  }
};

